import { CRUDMutations } from '@/utils/store-utilities'

/**
 * Default item factory for try-on product rows
 * @param {Object} item - Raw item data
 * @returns {Object} Normalized item
 */
const defaultItem = (item) => ({
  product_id: null,
  product_title: '',
  product_slug: '',
  variant_ids: [],
  option: null,
  option_value: null,
  try_on_enabled: false,
  try_on_media_id: null,
  thumbnail: null,
  gallery: [],
  ...item,
})

export const state = () => ({
  items: [],
  expandedRowId: null,
})

export const mutations = {
  ...CRUDMutations(defaultItem),

  /**
   * Set which row is expanded to show gallery
   * @param {Object} state - Vuex state
   * @param {string|null} rowId - Row identifier (product_id + option_value.id)
   */
  setExpandedRow(state, rowId) {
    state.expandedRowId = rowId
  },

  /**
   * Disable all try-on media for a given product
   * @param {Object} state - Vuex state
   * @param {number} productId - Product ID to disable all media for
   */
  disableAllMedia(state, productId) {
    state.items.forEach((item) => {
      if (item.product_id === productId) {
        item.try_on_enabled = false
        item.try_on_media_id = null
        if (item.gallery) {
          item.gallery.forEach((media) => {
            media.is_try_on_enabled = false
          })
        }
      }
    })
  },

  /**
   * Update a specific media item's try_on status in a row
   * @param {Object} state - Vuex state
   * @param {Object} payload - { rowId, mediaId, enabled }
   */
  updateMediaStatus(state, { rowId, mediaId, enabled }) {
    const row = state.items.find((item) => {
      const itemRowId = item.option_value ? `${item.product_id}-${item.option_value.id}` : `${item.product_id}`
      return itemRowId === rowId
    })
    if (!row) return

    // Update gallery items - disable all others if enabling one
    row.gallery.forEach((media) => {
      if (media.id === mediaId) {
        media.is_try_on_enabled = enabled
      } else if (enabled) {
        media.is_try_on_enabled = false
      }
    })

    // Update row-level status
    row.try_on_enabled = row.gallery.some((m) => m.is_try_on_enabled)
    row.try_on_media_id = enabled ? mediaId : null
  },
}

export const getters = {
  /**
   * Get all items
   * @param {Object} state - Vuex state
   * @returns {Array} List of items
   */
  list(state) {
    return state.items
  },

  /**
   * Check if a row is expanded
   * @param {Object} state - Vuex state
   * @returns {Function} Getter function
   */
  isRowExpanded: (state) => (rowId) => {
    return state.expandedRowId === rowId
  },
}

export const actions = {
  /**
   * Toggle the expanded state of a row
   * @param {Object} context - Vuex context
   * @param {string} rowId - Row identifier
   */
  toggleRowExpand({ commit, state }, rowId) {
    if (state.expandedRowId === rowId) {
      commit('setExpandedRow', null)
    } else {
      commit('setExpandedRow', rowId)
    }
  },

  /**
   * Toggle try-on status for a media item
   * @param {Object} context - Vuex context
   * @param {Object} payload - { productId, mediaId, enabled, rowId }
   * @returns {Promise} API response
   */
  async toggleMedia({ commit }, { productId, mediaId, enabled, rowId }) {
    const response = await this.$axios.$post(`admin/tryOn/products/${productId}/media/${mediaId}/toggle`, {
      enabled,
    })

    if (response) {
      commit('updateMediaStatus', { rowId, mediaId, enabled })
    }

    return response
  },

  /**
   * Disable all try-on media for a product in a single request
   * @param {Object} context - Vuex context
   * @param {Object} payload - { productId }
   * @returns {Promise} API response
   */
  async disableAll({ commit }, { productId }) {
    const response = await this.$axios.$post(`admin/tryOn/products/${productId}/disable-all`)

    if (response) {
      commit('disableAllMedia', productId)
    }

    return response
  },

  /**
   * Auto-select the best try-on image for a product using AI
   * @param {Object} context - Vuex context
   * @param {Object} payload - { productId }
   * @returns {Promise} API response with selection results
   */
  async autoSelect(_, { productId }) {
    const response = await this.$axios.$post(`admin/tryOn/products/${productId}/auto-select`)
    return response
  },
}
